import React from 'react';
import { CheckIcon } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';

interface Step {
  label: string;
}

interface Props {
  steps: Step[];
  /** 1-indexed current step. */
  current: number;
  /** Furthest step the user has reached. Steps up to it stay clickable after going back,
   *  so the caller must validate the current step before a forward jump. */
  reached?: number;
  onStepClick?: (step: number) => void;
}

const Stepper: React.FC<Props> = ({ steps, current, reached = current, onStepClick }) => (
  <nav aria-label="Progress" className="flex items-center justify-center">
    {steps.map((step, idx) => {
      const stepNum = idx + 1;
      const isActive = stepNum === current;
      const furthest = Math.max(current, reached);
      const isDone = !isActive && stepNum < furthest;
      const isLast = idx === steps.length - 1;
      const clickable = !isActive && stepNum <= furthest && !!onStepClick;
      const pillClasses = `flex shrink-0 items-center justify-center w-6 h-6 rounded-full border text-xs font-semibold ${
        isActive
          ? 'bg-primary text-primary-foreground border-primary'
          : clickable
            ? 'bg-muted text-foreground border-border'
            : 'bg-muted text-muted-foreground border-border'
      }`;
      const labelClasses = `flex items-center gap-2 text-sm font-medium ${
        isActive ? 'text-foreground' : 'text-muted-foreground'
      } ${clickable ? 'hover:text-foreground cursor-pointer' : ''}`;
      const inner = (
        <>
          <span className={pillClasses}>
            {isDone ? <CheckIcon className="size-3" strokeWidth={3} /> : stepNum}
          </span>
          {/* On a phone only the current step keeps its label, so the row fits. */}
          <span className={`whitespace-nowrap ${isActive ? '' : 'hidden sm:inline'}`}>
            {step.label}
          </span>
        </>
      );
      return (
        <React.Fragment key={step.label}>
          {clickable ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onStepClick!(stepNum)}
              aria-label={step.label}
              className={`${labelClasses} h-auto p-0 hover:bg-transparent`}
            >
              {inner}
            </Button>
          ) : (
            <div className={labelClasses} aria-current={isActive ? 'step' : undefined}>
              {inner}
            </div>
          )}
          {!isLast && (
            <div
              className={`flex-1 max-w-12 min-w-4 h-px mx-2 sm:mx-3.5 ${stepNum < furthest ? 'bg-foreground/40' : 'bg-border'}`}
              aria-hidden="true"
            />
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

export default Stepper;
